/**
 * Vendor Form - Create/Edit Vendor
 *
 * Form for creating new vendors or editing existing ones.
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-531
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

interface VendorFormData {
  name: string;
  legal_entity: string;
  headquarters_country: string;
  headquarters_city: string;
  website: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  description: string;
}

const initialFormData: VendorFormData = {
  name: '',
  legal_entity: '',
  headquarters_country: '',
  headquarters_city: '',
  website: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  description: '',
};

// Common countries for pharma vendors
const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Germany',
  'Switzerland',
  'France',
  'Japan',
  'China',
  'India',
  'Netherlands',
  'Belgium',
  'Ireland',
  'Denmark',
  'Sweden',
  'Canada',
  'Australia',
  'Singapore',
  'South Korea',
  'Israel',
  'Italy',
  'Spain',
  'Other',
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const VendorForm: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const isEditMode = vendorId && vendorId !== 'new';

  const [formData, setFormData] = useState<VendorFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [loadingVendor, setLoadingVendor] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Fetch existing vendor data in edit mode
  useEffect(() => {
    if (isEditMode && vendorId) {
      fetchVendor(vendorId);
    }
  }, [isEditMode, vendorId]);

  const fetchVendor = async (id: string) => {
    setLoadingVendor(true);
    try {
      const response = await fetch(`/api/vendors/${id}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Vendor not found');
        }
        throw new Error('Failed to fetch vendor');
      }
      const data = await response.json();
      const vendor = data.vendor || data;

      setFormData({
        name: vendor.name || '',
        legal_entity: vendor.legal_entity || '',
        headquarters_country: vendor.headquarters_country || '',
        headquarters_city: vendor.headquarters_city || '',
        website: vendor.website || '',
        contact_name: vendor.contact_name || '',
        contact_email: vendor.contact_email || '',
        contact_phone: vendor.contact_phone || '',
        description: vendor.description || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendor');
    } finally {
      setLoadingVendor(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error when field changes
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Vendor name is required';
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      errors.contact_email = 'Invalid email format';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      errors.website = 'Website must start with http:// or https://';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const url = isEditMode ? `/api/vendors/${vendorId}` : '/api/vendors';
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${isEditMode ? 'update' : 'create'} vendor`);
      }

      const data = await response.json();
      const newVendorId = data.vendor?.vendor_id || vendorId;

      // Navigate to the vendor detail page
      navigate(`/vendors/${newVendorId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendors');
  };

  if (loadingVendor) {
    return (
      <div className="vendor-form-container">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading vendor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-form-container">
      <div className="form-header">
        <button className="btn btn-ghost back-btn" onClick={handleCancel}>
          ← Back to Vendors
        </button>
        <h1>{isEditMode ? 'Edit Vendor' : 'Add New Vendor'}</h1>
        <p className="form-subtitle">
          {isEditMode
            ? 'Update vendor information and contact details'
            : 'Register a new AI/ML vendor for certification assessment'}
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">!</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="vendor-form">
        {/* Basic Information */}
        <section className="form-section">
          <h2 className="section-title">Basic Information</h2>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="name" className="required">
                Vendor Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Acme AI Solutions"
                className={validationErrors.name ? 'error' : ''}
              />
              {validationErrors.name && (
                <span className="field-error">{validationErrors.name}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="legal_entity">Legal Entity Name</label>
              <input
                type="text"
                id="legal_entity"
                name="legal_entity"
                value={formData.legal_entity}
                onChange={handleChange}
                placeholder="e.g., Acme AI Solutions Inc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
                className={validationErrors.website ? 'error' : ''}
              />
              {validationErrors.website && (
                <span className="field-error">{validationErrors.website}</span>
              )}
            </div>
          </div>
        </section>

        {/* Location */}
        <section className="form-section">
          <h2 className="section-title">Headquarters Location</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="headquarters_country">Country</label>
              <select
                id="headquarters_country"
                name="headquarters_country"
                value={formData.headquarters_country}
                onChange={handleChange}
              >
                <option value="">Select country...</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="headquarters_city">City</label>
              <input
                type="text"
                id="headquarters_city"
                name="headquarters_city"
                value={formData.headquarters_city}
                onChange={handleChange}
                placeholder="e.g., Boston"
              />
            </div>
          </div>
        </section>

        {/* Primary Contact */}
        <section className="form-section">
          <h2 className="section-title">Primary Contact</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact_name">Contact Name</label>
              <input
                type="text"
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="e.g., John Smith"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contact_email">Email</label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="john.smith@example.com"
                className={validationErrors.contact_email ? 'error' : ''}
              />
              {validationErrors.contact_email && (
                <span className="field-error">{validationErrors.contact_email}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact_phone">Phone</label>
              <input
                type="tel"
                id="contact_phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="form-section">
          <h2 className="section-title">Description</h2>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="description">About the Vendor</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Brief description of the vendor and their AI/ML products..."
              />
            </div>
          </div>
        </section>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-small" />
                {isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : isEditMode ? (
              'Save Changes'
            ) : (
              'Create Vendor'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VendorForm;
